import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdInsertLink, MdOutlineSearch } from 'react-icons/md';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import { useDebounce } from 'use-debounce';
import { useListGroups } from 'legacy/groups/hooks';
import ItemList, { Item } from '../ItemList';
import EmptyItemSelectionList from '../EmptyItemSelectionList';

const OrganizationSelectionModal = ({
  organizations,
  onClose,
  onSubmit
}: {
  organizations: Item[];
  onClose: () => void;
  onSubmit: (organizations: Item[]) => void;
}) => {
  const { t } = useTranslation(['events']);
  const [selectedOrganizations, setSelectedOrganizations] =
    useState<Item[]>(organizations);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch] = useDebounce(search, 200);
  const ref = useRef<IntersectionObserver>();

  const {
    data: searchedOrganizations,
    isLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage
  } = useListGroups({
    search: debouncedSearch
  });

  const handleSubmit = () => {
    if (search.length > 0) {
      setSearch('');
    } else {
      onSubmit(selectedOrganizations);
    }
  };

  const eligibleOrganizationsCount =
    selectedOrganizations.length > 0 ? `• ${selectedOrganizations.length}` : '';
  const footerText =
    search.length > 0
      ? t('settings.promoterOffers.selectionModals.organizations.saveSelection')
      : `${t('settings.promoterOffers.selectionModals.organizations.addEligible')} ${eligibleOrganizationsCount}`;

  const handleSearch = ({ currentTarget }) => {
    setSearch(currentTarget.value);
  };

  const showEmptyState =
    !isLoading && !debouncedSearch && !selectedOrganizations?.length;

  const handleSelectOrganization = (item: Item) => {
    const newSelectedOrganizations = selectedOrganizations.some(
      (o) => o.id === item.id
    )
      ? selectedOrganizations.filter((o) => o.id !== item.id)
      : [...selectedOrganizations, item];
    setSelectedOrganizations(newSelectedOrganizations);
  };

  const handleClearAll = () => {
    setSelectedOrganizations([]);
  };

  const lastElementRef = useCallback(
    (node: HTMLLIElement) => {
      if (isLoading || isFetchingNextPage) {
        return;
      }

      if (ref.current) {
        ref.current.disconnect();
      }

      ref.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });

      if (node) {
        ref.current.observe(node);
      }
    },
    [isLoading, hasNextPage]
  );

  const organizationsToDisplay =
    search.length > 0
      ? searchedOrganizations?.map((organization) => ({
          id: organization.id,
          name: organization.name,
          photoUrl: organization?.badgeImageUrl || ''
        }))
      : selectedOrganizations;

  return (
    <Modal
      visible
      autoHeight
      type={ModalType.SMALL}
      onClose={onClose}
      shouldStopModalClose={false}
      title={t('settings.promoterOffers.selectionModals.organizations.title')}
      icon={<MdInsertLink size={20} />}
      renderFooter={() => (
        <Button
          type={ButtonType.Primary}
          onClick={handleSubmit}
          loading={false}
        >
          {footerText}
        </Button>
      )}
    >
      <div className="px-4 pt-4 flex flex-col justify-between">
        <p className="mt-4 text-base font-normal">
          {t('settings.promoterOffers.selectionModals.organizations.description')}
        </p>
        <div className="relative shrink-0 flex h-12 border-1 border-gray-300 rounded-full mt-6">
          <MdOutlineSearch
            className="absolute left-3 text-gray-400 self-center justify-center"
            size={24}
          />
          <input
            type="text"
            className="w-full h-full px-11 rounded-full text-base text-gray-600 placeholder:text-gray-400"
            placeholder={t('settings.promoterOffers.selectionModals.organizations.searchPlaceholder')}
            value={search}
            onChange={handleSearch}
          />
        </div>
        <div>
          {showEmptyState ? (
            <EmptyItemSelectionList message={t('settings.promoterOffers.selectionModals.organizations.emptyState')} />
          ) : (
            <div className="overflow-y-auto mt-4">
              <ItemList
                isLoading={isLoading}
                items={organizationsToDisplay}
                onSelect={handleSelectOrganization}
                selectedItems={selectedOrganizations}
                search={search}
                onClearAll={handleClearAll}
                lastElementRef={lastElementRef}
                clearButtonText={`${selectedOrganizations.length} ${t('settings.promoterOffers.selectionModals.organizations.clearButton')}`}
              />
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default OrganizationSelectionModal;
